import React, { useState } from 'react';
import { Mail, User, Phone, MessageSquare, Briefcase, Send, CheckCircle } from 'lucide-react';
import { serviceTypes } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    website: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!/^[\d\s+()-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Veuillez sélectionner un service';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send to backend API
      const response = await axios.post(`${BACKEND_URL}/api/quote`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.serviceType,
        message: formData.message,
        website: formData.website
      });
      
      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: response.data.message
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          message: '',
          website: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.status === 429
          ? 'Trop de demandes envoyées. Merci de réessayer dans quelques minutes ou de nous appeler au 06 20 60 41 66.'
          : (error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="devis" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-[#01162D] mb-4">
            Demande de <span className="text-[#1F6FD6]">Devis Gratuit</span>
          </h2>
          <div className="w-24 h-1 bg-[#1F6FD6] mx-auto mb-6"></div>
          <p className="text-lg text-[#647A97] max-w-3xl mx-auto">
            Remplissez le formulaire ci-dessous et nous vous contacterons dans les 48h
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <div className="bg-[#EEF1F5] rounded-md p-5 md:p-8 shadow-lg relative overflow-hidden">
            <form onSubmit={handleSubmit} noValidate className="space-y-6" data-testid="quote-form">
              <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                <label htmlFor="website">Site web</label>
                <input type="text" id="website" name="website" tabIndex="-1" autoComplete="off" value={formData.website} onChange={handleChange} data-testid="quote-honeypot" />
              </div>
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="flex items-center gap-2 text-[#01162D] font-medium mb-2">
                  <User size={18} />
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.name ? 'border-red-500' : 'border-[#C9D6E6]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1F6FD6] transition-all duration-200`}
                  placeholder="Jean Dupont"
                  autoComplete="name"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1" data-testid="error-name">{errors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="flex items-center gap-2 text-[#01162D] font-medium mb-2">
                  <Mail size={18} />
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.email ? 'border-red-500' : 'border-[#C9D6E6]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1F6FD6] transition-all duration-200`}
                  placeholder="jean.dupont@email.com"
                  autoComplete="email"
                  inputMode="email"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1" data-testid="error-email">{errors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="flex items-center gap-2 text-[#01162D] font-medium mb-2">
                  <Phone size={18} />
                  Téléphone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.phone ? 'border-red-500' : 'border-[#C9D6E6]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1F6FD6] transition-all duration-200`}
                  placeholder="06 12 34 56 78"
                  autoComplete="tel"
                  inputMode="tel"
                  disabled={isSubmitting}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1" data-testid="error-phone">{errors.phone}</p>
                )}
              </div>

              {/* Service Type Field */}
              <div>
                <label htmlFor="serviceType" className="flex items-center gap-2 text-[#01162D] font-medium mb-2">
                  <Briefcase size={18} />
                  Type de service *
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.serviceType ? 'border-red-500' : 'border-[#C9D6E6]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1F6FD6] transition-all duration-200`}
                  disabled={isSubmitting}
                >
                  <option value="">Sélectionnez un service</option>
                  {serviceTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors.serviceType && (
                  <p className="text-red-500 text-sm mt-1" data-testid="error-serviceType">{errors.serviceType}</p>
                )}
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="flex items-center gap-2 text-[#01162D] font-medium mb-2">
                  <MessageSquare size={18} />
                  Décrivez votre projet *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.message ? 'border-red-500' : 'border-[#C9D6E6]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1F6FD6] transition-all duration-200 resize-none`}
                  placeholder="Décrivez votre projet en détail..."
                  disabled={isSubmitting}
                ></textarea>
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1" data-testid="error-message">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-[#1F6FD6] text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 transition-all duration-300 shadow-lg ${
                  isSubmitting
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[#01162D] hover:shadow-xl'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Envoyer ma demande
                  </>
                )}
              </button>
            </form>

            {/* Status Message */}
            {submitStatus && (
              <div
                className={`mt-6 p-4 rounded-lg flex items-start gap-3 ${
                  submitStatus.type === 'success'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                {submitStatus.type === 'success' && (
                  <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                )}
                <p
                  className={`${
                    submitStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {submitStatus.message}
                </p>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="space-y-8">
            <div className="bg-[#01162D] rounded-md p-6 md:p-8 text-white shadow-xl border-2 border-[#1F6FD6]">
              <h3 className="text-2xl font-bold mb-6 text-[#1F6FD6]">Pourquoi nous choisir ?</h3>
              <ul className="space-y-4">
                {[
                  'Devis gratuit et sans engagement',
                  'Réponse sous 48h garantie',
                  'Équipe de professionnels qualifiés',
                  'Matériaux de qualité premium',
                  'Garantie et SAV inclus',
                  'Implanté sur le bassin stéphanois'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#1F6FD6] flex-shrink-0 mt-0.5" />
                    <span className="text-[#9FB3CC]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#E3EDF9] border border-[#1F6FD6]/30 rounded-md p-6 md:p-8">
              <h3 className="text-xl font-bold text-[#01162D] mb-4">
                Besoin d'un renseignement ?
              </h3>
              <p className="text-[#01162D] mb-4">
                Notre équipe est à votre disposition pour répondre à toutes vos questions.
              </p>
              <div className="space-y-3">
                <a
                  href="tel:+33620604166"
                  className="flex items-center gap-3 text-[#01162D] hover:text-[#1F6FD6] transition-colors"
                >
                  <Phone size={18} />
                  <span className="font-medium">06 20 60 41 66</span>
                </a>
                <a
                  href="mailto:contact@sma-concept.fr"
                  className="flex items-center gap-3 text-[#01162D] hover:text-[#1F6FD6] transition-colors"
                >
                  <Mail size={18} />
                  <span className="font-medium">contact@sma-concept.fr</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
